import { useTranslate } from "@refinedev/core";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { useNotificationSendLogsQuery } from "@modules/notifications/services/mutations";

const STATUS_COLOR = { success: "success", error: "error", skipped: "default" } as const;

interface Props {
  notificationId: number | null;
  onClose: () => void;
}

export function NotificationSendLogsDialog({ notificationId, onClose }: Props) {
  const translate = useTranslate();
  const t = (key: string) => translate(`notifications_emails_admin.send_logs.${key}`);
  const { data: logs = [], isLoading } = useNotificationSendLogsQuery(notificationId);

  return (
    <Dialog open={notificationId !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("title")}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <CircularProgress />
        ) : !logs.length ? (
          <Typography color="text.secondary">{t("empty")}</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("date")}</TableCell>
                <TableCell>{t("channel")}</TableCell>
                <TableCell>{t("target")}</TableCell>
                <TableCell>{t("status")}</TableCell>
                <TableCell>{t("detail")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {new Date(log.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                  </TableCell>
                  <TableCell>
                    {log.channel}
                    {log.dry_run && ` (${t("dry_run")})`}
                  </TableCell>
                  <TableCell>{log.target}</TableCell>
                  <TableCell>
                    <Chip size="small" color={STATUS_COLOR[log.status]} label={log.status} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {log.detail || log.message_id}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
